export const content = {
  en: {
    nav: { home: "HOME", about: "ABOUT", services: "SERVICES", gallery: "GALLERY", packages: "PACKAGES", faq: "FAQ", contact: "CONTACT" },
    quoteCta: "GET A QUOTE",
    marquee: ["ELEVATED COCKTAILS", "TIMELESS HOSPITALITY", "HUDSON & STIR", "NEW YORK CITY", "CRAFT & PRECISION"],
    hero: {
      eyebrow: "LUXURY MOBILE BAR",
      line1: "BAR",
      line2: "EXPERIENCES",
      sub: "Elevated cocktails. Timeless hospitality. Wherever you celebrate.",
      primary: "GET A QUOTE",
      secondary: "EXPLORE OUR SERVICES",
      side: "EST. NEW YORK CITY",
      scroll: "SCROLL",
      imgAlt: "Hudson & Stir Mobile Bar setup overlooking Manhattan and Hudson Yards at golden hour",
    },
    about: {
      label: "ABOUT US",
      pre: "Crafting ",
      em: "unforgettable",
      post: " moments.",
      body: "Hudson & Stir Mobile Bar brings a refined mobile bar experience directly to your event. From intimate celebrations to weddings and corporate gatherings, we combine craft cocktails, thoughtful service and elevated presentation in every detail.",
      cta: "OUR STORY",
      imgAlt: "Elegant white Hudson & Stir mobile bar setup with gold branding at a private event",
    },
    services: {
      label: "SERVICES",
      pre: "Tailored bar experiences\nfor ",
      em: "every occasion.",
      sub: "Five signature services, one standard — every detail considered, every guest remembered.",
      cta: "VIEW PACKAGES",
      items: [
        { title: "WEDDINGS", desc: "Thoughtful bar service crafted around your celebration." },
        { title: "CORPORATE EVENTS", desc: "Polished hospitality designed to reflect the standard of your brand." },
        { title: "PRIVATE PARTIES", desc: "Elevated cocktails and seamless service for intimate celebrations." },
        { title: "HOLIDAY PARTIES", desc: "Seasonal cocktails and warm hospitality for unforgettable gatherings." },
        { title: "COCKTAIL EXPERIENCES", desc: "Interactive cocktail moments designed to entertain, engage and impress." },
      ],
    },
    gallery: {
      label: "GALLERY",
      pre: "Moments, ",
      em: "beautifully",
      post: " served.",
      cta: "VIEW FULL GALLERY",
      pageTitlePre: "The ",
      pageTitleEm: "Gallery.",
      pageSub: "Warm light, polished glass, and evenings worth remembering — a glimpse of the Hudson & Stir experience across New York City.",
      pageCta: "Your event could be next.",
      frameAria: "Featured photograph",
      images: {
        setup: { alt: "Hudson & Stir mobile bar setup with gold bar tools, glassware and signature cocktail menu at an outdoor event", caption: "The Setup" },
        experience: { alt: "Hudson & Stir branded glasses, napkins and bar accessories at an event", caption: "The Experience" },
        craft: { alt: "Hudson & Stir bartender preparing a handcrafted cocktail in a branded apron", caption: "The Craft" },
        cocktail: { alt: "Colorful signature cocktails presented beside the Hudson & Stir menu", caption: "The Cocktail" },
        details: { alt: "Hudson & Stir signature cocktail menu with branded napkins and fresh florals", caption: "The Details" },
        rooftop: { alt: "Hudson & Stir Mobile Bar setup overlooking Manhattan and Hudson Yards at golden hour", caption: "The Rooftop" },
        whitebar: { alt: "Elegant Hudson & Stir mobile bar setup for a private event", caption: "The Bar" },
        evening: { alt: "Evening signature cocktails beside the framed Hudson & Stir menu", caption: "The Evening" },
        inspiration: { alt: "Architectural sketch of The Vessel at Hudson Yards, the inspiration behind Hudson & Stir", caption: "The Inspiration" },
      },
    },
    experience: {
      label: "THE EXPERIENCE",
      pre: "The Hudson ",
      em: "&",
      post: " Stir Experience",
      steps: [
        { n: "01", title: "INQUIRE", desc: "Tell us about your event." },
        { n: "02", title: "PLAN", desc: "We'll customize every detail around your needs." },
        { n: "03", title: "PREPARE", desc: "We handle the logistics so you don't have to." },
        { n: "04", title: "CELEBRATE", desc: "Sit back, relax, and enjoy the experience." },
        { n: "05", title: "UNFORGETTABLE", desc: "Memories made. Glasses raised." },
      ],
    },
    packages: {
      label: "PACKAGES",
      pre: "Three ways to ",
      em: "raise the bar.",
      serviceWord: "SERVICE",
      cta: "GET A QUOTE",
      items: [
        {
          n: "01",
          name: "BARTENDER SERVICE",
          price: "From $450",
          capacity: "Up to 40 guests • Up to 4 hours of active bar service",
          tagline: "For hosts who already have the bar covered and simply need professional service.",
          features: ["One professional bartender", "Professional bar tools and mixology equipment", "Alcohol shopping guidance", "Cocktail menu planning", "Bar setup, breakdown, and cleanup of the service area"],
          client: "Client provides: A suitable bar or work surface, all alcohol, and all consumables, including ice, mixers, garnishes, drinkware, napkins, straws, and printed menus.",
        },
        {
          n: "02",
          name: "ESSENTIAL BAR SERVICE",
          price: "From $795",
          capacity: "Up to 40 guests • Up to 4 hours of active bar service",
          tagline: "A polished cocktail service with the essentials handled for you.",
          features: ["One professional bartender", "Professional bar tools and mixology equipment", "High-quality mixers", "Fresh juices, house-made syrups, and premium fresh garnishes", "Ice", "Standard disposable drinkware, cocktail napkins, and straws", "Alcohol shopping guidance", "Cocktail menu planning", "Up to 2 personalized signature cocktails", "Up to 2 pre-selected classic cocktails", "Framed custom cocktail menu", "Bar setup, breakdown, and cleanup of the service area", "Premium disposable drinkware is available as an add-on."],
          client: "Client provides: A suitable bar or work surface and all alcohol.",
        },
        {
          n: "03",
          name: "HUDSON & STIR EXPERIENCE",
          price: "From $1,295",
          capacity: "Up to 40 guests • Up to 4 hours of active bar service",
          badge: "THE SIGNATURE EXPERIENCE",
          tagline: "Our complete mobile bar experience, designed to look as exceptional as it serves.",
          features: ["Hudson & Stir portable bar", "One professional bartender", "Professional bar tools and mixology equipment", "High-quality mixers", "Fresh juices, house-made syrups, and premium fresh garnishes", "Ice", "Premium disposable drinkware, cocktail napkins, and straws", "Alcohol shopping guidance", "Cocktail menu planning", "Up to 3 personalized signature cocktails", "Up to 3 pre-selected classic cocktails", "One signature selection may be zero-proof", "Framed custom cocktail menu", "Standard bar-top styling", "Bar setup, breakdown, and cleanup of the service area"],
          client: "Client provides: All alcohol.",
        },
      ],
      custom: {
        label: "WEDDINGS & LARGE EVENTS",
        pre: "Designed around ",
        em: "your event.",
        body: "Weddings, corporate receptions and larger celebrations may require additional bartenders, barbacks, multiple bar stations and customized logistics. Tell us about your guest count, venue and vision, and we'll build the right service around it.",
        cta: "REQUEST A CUSTOM QUOTE",
      },
      dryHire: "Hudson & Stir Mobile Bar operates as a dry-hire bar service — alcohol is provided by the client, and we help guide quantities and selections.",
      disclaimer: "Starting prices apply to standard events of up to 40 guests and up to 4 hours of active bar service. Setup and breakdown are completed outside the service window. Additional guests, service time, staffing, travel, tolls, paid parking, and specialty requests are quoted separately. The client purchases and provides all alcohol. Applicable sales tax is added based on the event location. Gratuity is not included.",
    },
    ctaSection: {
      line1: "READY TO ELEVATE",
      line2: "YOUR NEXT EVENT?",
      sub: "Let's create something unforgettable together.",
      button: "GET A QUOTE",
    },
    faq: {
      label: "FAQ",
      pre: "Frequently asked\n",
      em: "questions.",
      sub: "Everything you need to know about bringing the Hudson & Stir experience to your event.",
      items: [
        { q: "Does Hudson & Stir provide alcohol?", a: "No. Hudson & Stir operates as a dry-hire mobile bar service. Clients provide alcoholic beverages, and we provide guidance on quantities and selections." },
        { q: "How many guests can you serve?", a: "Our standard package pricing covers up to 40 guests. Larger events are welcome and are custom quoted based on guest count, menu, staffing, venue, and logistics." },
        { q: "How long is service?", a: "Standard packages include up to 4 hours of active bar service. Setup and breakdown take place outside the contracted service window, and service begins at the contracted start time." },
        { q: "Do you provide the bar itself?", a: "The Hudson & Stir portable bar is included in the Hudson & Stir Experience and may be added to eligible services." },
        { q: "Do you provide mixers, ice and garnishes?", a: "They are included in the Essential Bar Service and Hudson & Stir Experience according to the agreed menu. Bartender Service is labor-focused and the client provides consumables." },
        { q: "Do you provide glassware?", a: "Premium disposable serviceware is included where specified. Real glassware can be arranged separately and is custom quoted." },
        { q: "Do you travel?", a: "Yes. Hudson & Stir serves New York City, Long Island and select Tri-State locations. Travel fees, tolls and parking may apply depending on venue location." },
        { q: "How do I reserve my date?", a: "A signed service agreement and a 50% booking retainer are required to reserve your date. The remaining balance is due 7 days before the event, and the final guest count is confirmed at the same time." },
        { q: "What is your cancellation & rescheduling policy?", a: "Booking retainers are non-refundable but may be transferred once to another available date within 12 months. Full booking and cancellation terms are provided in the service agreement." },
        { q: "What drinks can the bartender serve?", a: "Our bartenders can serve beer, wine, simple mixed drinks and a selection of classic cocktails based on your agreed beverage menu and available ingredients. Signature cocktails are curated selections created or customized specifically for your event. More extensive or highly specialized cocktail menus may require additional planning or staffing." },
        { q: "Is gratuity included?", a: "Package pricing does not include gratuity. Voluntary guest tipping (a tip jar or QR code, where the venue permits) and a Hosted Service Charge are different arrangements — a service charge is a predetermined amount agreed with you as the host, never a voluntary gratuity. The arrangement selected for your event, and any amounts, are confirmed in your quote and Service Agreement." },
        { q: "What happens for outdoor events if the weather changes?", a: "Outdoor events require an appropriate covered backup area provided by the client or venue. Weather-related changes and rescheduling are handled according to availability and the service agreement." },
      ],
    },
    footer: {
      blurb: "A luxury mobile bar and bartending experience serving New York City, Long Island and select Tri-State locations. Elevated cocktails, timeless hospitality.",
      explore: "EXPLORE",
      contact: "CONTACT",
      begin: "BEGIN",
      rights: "© 2026 HUDSON & STIR MOBILE BAR. ALL RIGHTS RESERVED.",
      dryHire: "DRY-HIRE BAR SERVICE — ALCOHOL IS PROVIDED BY THE CLIENT.",
      privacy: "PRIVACY NOTICE",
    },
    quote: {
      stepWord: "STEP",
      stepLabels: ["EVENT", "DATE", "LOCATION", "GUESTS", "SERVICE", "DURATION", "VENUE", "ENHANCEMENTS", "DETAILS", "CONTACT", "REVIEW"],
      s0: { title: "What type of event?", hint: "Select the occasion closest to yours." },
      eventTypes: [
        { value: "Wedding", label: "Wedding" },
        { value: "Corporate Event", label: "Corporate Event" },
        { value: "Private Party", label: "Private Party" },
        { value: "Birthday", label: "Birthday" },
        { value: "Holiday Party", label: "Holiday Party" },
        { value: "Cocktail Experience", label: "Cocktail Experience" },
        { value: "Other", label: "Other" },
      ],
      s1: { title: "When is your event?", hint: "An estimate is perfectly fine.", dateLabel: "SELECT DATE", timeLabel: "APPROXIMATE START TIME", optional: "(OPTIONAL)", flexible: "My date is flexible" },
      s2: { title: "Where will it take place?", hint: "Venue, neighborhood, or city — we serve NYC, Long Island and select Tri-State locations.", locationPh: "e.g. Tribeca rooftop, Manhattan", zipPh: "City / ZIP code" },
      s3: { title: "Estimated guest count" },
      guestCounts: [
        { value: "Under 30", label: "Under 30" },
        { value: "30–50", label: "30–50" },
        { value: "51–75", label: "51–75" },
        { value: "76–100", label: "76–100" },
        { value: "101–150", label: "101–150" },
        { value: "150+", label: "150+" },
      ],
      s4: { title: "Which service are you considering?", hint: "Choose what feels closest — we'll refine it together." },
      services: [
        { value: "Bartender Service", label: "Bartender Service", price: "FROM $450" },
        { value: "Essential Bar Service", label: "Essential Bar Service", price: "FROM $795" },
        { value: "Hudson & Stir Experience", label: "Hudson & Stir Experience", price: "FROM $1,295" },
        { value: "Wedding / Large Event", label: "Wedding / Large Event", price: "CUSTOM QUOTE" },
        { value: "Not Sure Yet", label: "Not Sure Yet", price: "WE'LL GUIDE YOU" },
      ],
      s5: { title: "How long will you need us?", hint: "Hours of active bar service — setup and breakdown happen outside this window." },
      durations: [
        { value: "Up to 4 hours", label: "Up to 4 hours" },
        { value: "5 hours", label: "5 hours" },
        { value: "6 hours", label: "6 hours" },
        { value: "7+ hours / Custom", label: "7+ hours / Custom" },
      ],
      s6: {
        title: "A few venue details.",
        hint: "This helps us plan the right setup.",
        barLabel: "IS THERE A BAR OR TABLE AT THE VENUE?",
        barOptions: [
          { value: "Yes", label: "Yes" },
          { value: "No", label: "No" },
          { value: "Not sure", label: "Not sure" },
        ],
        settingLabel: "INDOOR OR OUTDOOR?",
        settingOptions: [
          { value: "Indoor", label: "Indoor" },
          { value: "Outdoor", label: "Outdoor" },
          { value: "Both", label: "Both" },
          { value: "Not sure", label: "Not sure" },
        ],
      },
      s7: { title: "Interested in any enhancements?", hint: "Optional — select anything that interests you. Pricing is confirmed during planning.", includedNote: "PORTABLE BAR — INCLUDED WITH YOUR SERVICE" },
      enhancements: [
        { value: "Portable Bar", label: "PORTABLE BAR" },
        { value: "Additional Signature Cocktail", label: "ADDITIONAL SIGNATURE COCKTAIL" },
        { value: "Zero-Proof Cocktail", label: "ZERO-PROOF COCKTAIL" },
        { value: "Espresso Martini Service", label: "ESPRESSO MARTINI SERVICE" },
        { value: "Champagne Toast", label: "CHAMPAGNE TOAST" },
        { value: "Real Glassware", label: "REAL GLASSWARE" },
        { value: "Not Sure Yet", label: "NOT SURE YET" },
        { value: "None", label: "NO ENHANCEMENTS" },
      ],
      s8: { title: "Tell us about your event.", hint: "The atmosphere you're imagining, beverage styles or cocktail preferences, the moments that matter — anything helps.", optional: "OPTIONAL", placeholder: "We're planning…" },
      s9: {
        title: "Your contact information",
        firstPh: "First name",
        lastPh: "Last name",
        emailPh: "Email address",
        phonePh: "Phone (optional)",
        hint: "Please add your name and a valid email address so we can reach you.",
      },
      s10: {
        title: "Review your inquiry",
        hint: "A final look before we begin planning.",
        edit: "EDIT",
        rows: { event: "EVENT", date: "DATE", location: "LOCATION", guests: "GUESTS", service: "SERVICE", duration: "DURATION", venue: "VENUE", extras: "EXTRAS", name: "NAME", email: "EMAIL", phone: "PHONE" },
        flexibleSuffix: "(flexible)",
        barPrefix: "Bar/table:",
        note: "Hudson & Stir Mobile Bar is a dry-hire bar service — alcohol is provided by the client. Submitting an inquiry does not reserve your event date; a signed service agreement and booking retainer confirm your date.",
        privacyPre: "By submitting this inquiry you acknowledge our",
        privacyLink: "Privacy Notice.",
      },
      buttons: { back: "BACK", continue: "CONTINUE", submit: "SUBMIT INQUIRY", sending: "SENDING…" },
      sidebar: {
        quote: "Every great evening begins with a conversation.",
        body: "Share a few details about your event and we'll respond with a tailored proposal. No accounts, no payments — just the beginning of something memorable.",
      },
      confirm: {
        titlePre: "THANK ",
        titleEm: "YOU",
        line1: "Thank you for considering Hudson & Stir.",
        line2: "We've received your event details and will be in touch to discuss your celebration.",
        ref: "REFERENCE",
        home: "RETURN HOME",
        gallery: "VIEW GALLERY",
      },
      error: "Something went wrong sending your inquiry. Please try again.",
      venueJoin: " · ",
    },
    gate: {
      brand: "HUDSON & STIR",
      line1: "Choose your preferred language",
      line2: "Elige tu idioma de preferencia",
      en: "English",
      es: "Español",
      label: "Language selection",
    },
    privacy: {
      titlePre: "Privacy ",
      titleEm: "Notice",
      intro: "How Hudson & Stir Mobile Bar handles the information you share with us.",
      updated: "LAST UPDATED: AUGUST 2026",
      sections: [
        { h: "WHAT WE COLLECT", body: "When you submit an inquiry through our quote form, we collect the details you provide: your name, email address, phone number (optional), your preferred language, and your event details — event type, date and timing, location, guest count, service and enhancement selections, and any notes you choose to share." },
        { h: "HOW WE USE IT", body: "We use this information only to respond to your inquiry, prepare a tailored proposal, plan and deliver your event, and manage our service relationship with you. We do not add you to marketing lists, and this site does not use advertising trackers." },
        { h: "WHO HANDLES IT", body: "A small number of service providers process your information on our behalf: Resend delivers our inquiry notification and acknowledgment emails, and our website hosting provider stores form submissions in our database. We also use PostHog analytics to understand how the site is used — pages visited and interactions such as button clicks — which may set a cookie or similar identifier in your browser." },
        { h: "HOW LONG WE KEEP IT", body: "We keep inquiry information for as long as needed to respond to your request, plan and deliver your event, and meet our ordinary record-keeping needs." },
        { h: "YOUR CHOICES", body: "You may ask us to access, correct, or delete the information you have shared with us at any time by emailing us. We will respond to reasonable requests promptly." },
        { h: "CONTACT", body: "Hudson & Stir Mobile Bar · hello@hudsonandstir.com · New York City, Long Island and select Tri-State locations." },
      ],
    },
    lightbox: { close: "Close gallery", prev: "Previous image", next: "Next image", view: "View larger image:" },
    aria: { openMenu: "Open menu", closeMenu: "Close menu", homeLogo: "Hudson & Stir Mobile Bar home" },
  },

  es: {
    nav: { home: "INICIO", about: "NOSOTROS", services: "SERVICIOS", gallery: "GALERÍA", packages: "PAQUETES", faq: "PREGUNTAS", contact: "CONTACTO" },
    quoteCta: "SOLICITAR COTIZACIÓN",
    marquee: ["CÓCTELES ELEVADOS", "HOSPITALIDAD ATEMPORAL", "HUDSON & STIR", "NUEVA YORK", "ARTESANÍA Y PRECISIÓN"],
    hero: {
      eyebrow: "BAR MÓVIL DE LUJO",
      line1: "EXPERIENCIAS",
      line2: "DE BAR",
      sub: "Cócteles elevados. Hospitalidad atemporal. Donde sea que celebres.",
      primary: "SOLICITAR COTIZACIÓN",
      secondary: "EXPLORA NUESTROS SERVICIOS",
      side: "NUEVA YORK",
      scroll: "DESLIZA",
      imgAlt: "Barra móvil Hudson & Stir con vista a Manhattan y Hudson Yards al atardecer",
    },
    about: {
      label: "NOSOTROS",
      pre: "Creamos momentos ",
      em: "inolvidables.",
      post: "",
      body: "Hudson & Stir Mobile Bar lleva una experiencia de bar refinada directamente a tu evento. Desde celebraciones íntimas hasta bodas y eventos corporativos, combinamos cócteles artesanales, servicio atento y una presentación elevada en cada detalle.",
      cta: "NUESTRA HISTORIA",
      imgAlt: "Elegante barra móvil blanca de Hudson & Stir con detalles dorados en un evento privado",
    },
    services: {
      label: "SERVICIOS",
      pre: "Experiencias de bar a la medida\npara ",
      em: "cada ocasión.",
      sub: "Cinco servicios insignia, un mismo estándar: cada detalle considerado, cada invitado atendido.",
      cta: "VER PAQUETES",
      items: [
        { title: "BODAS", desc: "Servicio de bar cuidadoso, creado en torno a tu celebración." },
        { title: "EVENTOS CORPORATIVOS", desc: "Hospitalidad impecable, diseñada para reflejar el nivel de tu marca." },
        { title: "FIESTAS PRIVADAS", desc: "Cócteles elevados y servicio impecable para celebraciones íntimas." },
        { title: "FIESTAS DE TEMPORADA", desc: "Cócteles de temporada y cálida hospitalidad para reuniones inolvidables." },
        { title: "EXPERIENCIAS DE CÓCTELES", desc: "Momentos interactivos de cócteles diseñados para sorprender y deleitar." },
      ],
    },
    gallery: {
      label: "GALERÍA",
      pre: "Momentos, ",
      em: "bellamente",
      post: " servidos.",
      cta: "VER GALERÍA COMPLETA",
      pageTitlePre: "La ",
      pageTitleEm: "Galería.",
      pageSub: "Luz cálida, cristal pulido y veladas para recordar: una mirada a la experiencia Hudson & Stir en la ciudad de Nueva York.",
      pageCta: "Tu evento podría ser el próximo.",
      frameAria: "Fotografía destacada",
      images: {
        setup: { alt: "Montaje de la barra móvil Hudson & Stir con herramientas doradas, cristalería y menú de cócteles en un evento al aire libre", caption: "El Montaje" },
        experience: { alt: "Vasos, servilletas y accesorios de bar con la marca Hudson & Stir en un evento", caption: "La Experiencia" },
        craft: { alt: "Bartender de Hudson & Stir preparando un cóctel artesanal con mandil de la marca", caption: "El Oficio" },
        cocktail: { alt: "Cócteles insignia de colores presentados junto al menú Hudson & Stir", caption: "El Cóctel" },
        details: { alt: "Menú de cócteles Hudson & Stir con servilletas de la marca y flores frescas", caption: "Los Detalles" },
        rooftop: { alt: "Barra móvil Hudson & Stir con vista a Manhattan y Hudson Yards al atardecer", caption: "La Azotea" },
        whitebar: { alt: "Elegante barra móvil Hudson & Stir montada para un evento privado", caption: "La Barra" },
        evening: { alt: "Cócteles insignia de noche junto al menú enmarcado de Hudson & Stir", caption: "La Noche" },
        inspiration: { alt: "Boceto arquitectónico de The Vessel en Hudson Yards, la inspiración de Hudson & Stir", caption: "La Inspiración" },
      },
    },
    experience: {
      label: "LA EXPERIENCIA",
      pre: "La Experiencia ",
      em: "Hudson & Stir",
      post: "",
      steps: [
        { n: "01", title: "CONSULTA", desc: "Cuéntanos sobre tu evento." },
        { n: "02", title: "PLANIFICA", desc: "Personalizamos cada detalle según tus necesidades." },
        { n: "03", title: "PREPARAMOS", desc: "Nos encargamos de la logística para que tú no tengas que hacerlo." },
        { n: "04", title: "CELEBRA", desc: "Relájate y disfruta de la experiencia." },
        { n: "05", title: "INOLVIDABLE", desc: "Recuerdos creados. Copas en alto." },
      ],
    },
    packages: {
      label: "PAQUETES",
      pre: "Tres formas de ",
      em: "elevar el listón.",
      serviceWord: "SERVICIO",
      cta: "SOLICITAR COTIZACIÓN",
      items: [
        {
          n: "01",
          name: "BARTENDER SERVICE",
          price: "Desde $450",
          capacity: "Hasta 40 invitados • Hasta 4 horas de servicio activo de bar",
          tagline: "Para anfitriones que ya tienen la barra cubierta y solo necesitan servicio profesional.",
          features: ["Un bartender profesional", "Herramientas profesionales de bar y equipo de mixología", "Guía para la compra de alcohol", "Planificación del menú de cócteles", "Montaje, desmontaje y limpieza del área de servicio del bar"],
          client: "El cliente proporciona: Una barra o superficie de trabajo adecuada, todo el alcohol y todos los consumibles, incluidos hielo, mixers, guarniciones, vasos, servilletas, pajillas y menús impresos.",
        },
        {
          n: "02",
          name: "ESSENTIAL BAR SERVICE",
          price: "Desde $795",
          capacity: "Hasta 40 invitados • Hasta 4 horas de servicio activo de bar",
          tagline: "Un servicio de cócteles impecable con todo lo esencial cubierto.",
          features: ["Un bartender profesional", "Herramientas profesionales de bar y equipo de mixología", "Mixers de alta calidad", "Jugos frescos, siropes artesanales y guarniciones frescas premium", "Hielo", "Vasos desechables estándar, servilletas de cóctel y pajillas", "Guía para la compra de alcohol", "Planificación del menú de cócteles", "Hasta 2 cócteles de autor personalizados", "Hasta 2 cócteles clásicos preseleccionados", "Menú de cócteles personalizado enmarcado", "Montaje, desmontaje y limpieza del área de servicio del bar", "Vasos desechables premium disponibles como add-on."],
          client: "El cliente proporciona: Una barra o superficie de trabajo adecuada y todo el alcohol.",
        },
        {
          n: "03",
          name: "HUDSON & STIR EXPERIENCE",
          price: "Desde $1,295",
          capacity: "Hasta 40 invitados • Hasta 4 horas de servicio activo de bar",
          badge: "LA EXPERIENCIA INSIGNIA",
          tagline: "Nuestra experiencia completa de bar móvil, diseñada para verse tan excepcional como sirve.",
          features: ["Barra portátil Hudson & Stir", "Un bartender profesional", "Herramientas profesionales de bar y equipo de mixología", "Mixers de alta calidad", "Jugos frescos, siropes artesanales y guarniciones frescas premium", "Hielo", "Vasos desechables premium, servilletas para cóctel y pajillas", "Guía para la compra de alcohol", "Planificación del menú de cócteles", "Hasta 3 cócteles de autor personalizados", "Hasta 3 cócteles clásicos preseleccionados", "Una de las tres selecciones de autor puede ser sin alcohol", "Menú de cócteles personalizado enmarcado", "Decoración estándar de la barra", "Montaje, desmontaje y limpieza del área de servicio del bar"],
          client: "El cliente proporciona: Todo el alcohol.",
        },
      ],
      custom: {
        label: "BODAS Y EVENTOS GRANDES",
        pre: "Diseñado en torno a ",
        em: "tu evento.",
        body: "Las bodas, recepciones corporativas y celebraciones más grandes pueden requerir bartenders adicionales, barbacks, varias estaciones de bar y logística personalizada. Cuéntanos tu número de invitados, el lugar y tu visión, y crearemos el servicio adecuado para ti.",
        cta: "SOLICITA UNA COTIZACIÓN PERSONALIZADA",
      },
      dryHire: "Hudson & Stir Mobile Bar opera como un servicio de bar dry-hire: el cliente proporciona todas las bebidas alcohólicas, y nosotros orientamos sobre cantidades y selecciones.",
      disclaimer: "Los precios iniciales aplican a eventos estándar de hasta 40 invitados y hasta 4 horas de servicio activo de bar. El montaje y el desmontaje se realizan fuera del horario de servicio. Los invitados adicionales, el tiempo adicional, el personal, el transporte, los peajes, el estacionamiento de pago y las solicitudes especiales se cotizan por separado. El cliente compra y proporciona todo el alcohol. El impuesto sobre las ventas aplicable se añade según la ubicación del evento. La propina no está incluida.",
    },
    ctaSection: {
      line1: "¿LISTO PARA ELEVAR",
      line2: "TU PRÓXIMO EVENTO?",
      sub: "Creemos juntos algo inolvidable.",
      button: "SOLICITAR COTIZACIÓN",
      imgAlt: "Cócteles insignia de noche junto al menú enmarcado de Hudson & Stir",
    },
    faq: {
      label: "PREGUNTAS",
      pre: "Preguntas ",
      em: "frecuentes.",
      sub: "Todo lo que necesitas saber para llevar la experiencia Hudson & Stir a tu evento.",
      items: [
        { q: "¿Hudson & Stir proporciona el alcohol?", a: "No. Hudson & Stir opera como un servicio de bar móvil dry-hire. El cliente proporciona todas las bebidas alcohólicas, y nosotros orientamos sobre cantidades y selecciones." },
        { q: "¿Para cuántos invitados pueden trabajar?", a: "Nuestros precios estándar cubren hasta 40 invitados. Los eventos más grandes son bienvenidos y se cotizan de forma personalizada según el número de invitados, el menú, el personal, el lugar y la logística." },
        { q: "¿Cuánto dura el servicio?", a: "Los paquetes estándar incluyen hasta 4 horas de servicio activo de bar. El montaje y el desmontaje se realizan fuera del horario contratado, y el servicio comienza a la hora acordada." },
        { q: "¿Proporcionan la barra?", a: "La barra portátil Hudson & Stir está incluida en la Hudson & Stir Experience y puede añadirse a los servicios elegibles." },
        { q: "¿Incluyen mixers, hielo y guarniciones?", a: "Están incluidos en el Essential Bar Service y en la Hudson & Stir Experience según el menú acordado. El Bartender Service se centra en la mano de obra y el cliente proporciona los consumibles." },
        { q: "¿Proporcionan cristalería?", a: "Los desechables premium están incluidos donde se especifica. La cristalería real puede coordinarse por separado con cotización personalizada." },
        { q: "¿Se desplazan?", a: "Sí. Hudson & Stir atiende la ciudad de Nueva York, Long Island y ubicaciones selectas del área Tri-State. Pueden aplicar cargos por transporte, peajes y estacionamiento según la ubicación del lugar." },
        { q: "¿Cómo reservo mi fecha?", a: "Se requiere un acuerdo de servicio firmado y un anticipo del 50% para reservar tu fecha. El saldo restante vence 7 días antes del evento, y el número final de invitados se confirma al mismo tiempo." },
        { q: "¿Cuál es la política de cancelación y reprogramación?", a: "El anticipo de reserva no es reembolsable, pero puede transferirse una vez a otra fecha disponible dentro de los 12 meses. Los términos completos de reserva y cancelación se incluyen en el acuerdo de servicio." },
        { q: "¿Qué bebidas puede servir el bartender?", a: "Nuestros bartenders pueden servir cerveza, vino, tragos sencillos y una selección de cócteles clásicos según el menú de bebidas acordado y los ingredientes disponibles. Los Signature Cocktails son selecciones curadas o creadas específicamente para tu evento. Los menús más extensos o especializados pueden requerir planificación o personal adicional." },
        { q: "¿La propina está incluida?", a: "El precio de los paquetes no incluye propina. La propina voluntaria de los invitados (recipiente o código QR, según lo permita el lugar) y un Hosted Service Charge son acuerdos distintos: un cargo por servicio es un monto predeterminado acordado contigo como anfitrión, nunca una propina voluntaria. El acuerdo seleccionado para tu evento y sus montos se confirman en tu cotización y en el Acuerdo de Servicio." },
        { q: "¿Qué pasa con eventos al aire libre si cambia el clima?", a: "Los eventos al aire libre requieren un área cubierta de respaldo proporcionada por el cliente o el lugar. Los cambios por clima y la reprogramación se manejan según la disponibilidad y el acuerdo de servicio." },
      ],
    },
    footer: {
      blurb: "Una experiencia de bar móvil de lujo y bartending que atiende la ciudad de Nueva York, Long Island y ubicaciones selectas del área Tri-State. Cócteles elevados, hospitalidad atemporal.",
      explore: "EXPLORA",
      contact: "CONTACTO",
      begin: "COMENZAR",
      rights: "© 2026 HUDSON & STIR MOBILE BAR. TODOS LOS DERECHOS RESERVADOS.",
      dryHire: "SERVICIO DE BAR DRY-HIRE — EL CLIENTE PROPORCIONA LAS BEBIDAS ALCOHÓLICAS.",
      privacy: "AVISO DE PRIVACIDAD",
    },
    quote: {
      stepWord: "PASO",
      stepLabels: ["EVENTO", "FECHA", "UBICACIÓN", "INVITADOS", "SERVICIO", "DURACIÓN", "LUGAR", "ADICIONALES", "DETALLES", "CONTACTO", "REVISIÓN"],
      s0: { title: "¿Qué tipo de evento es?", hint: "Selecciona la ocasión más cercana a la tuya." },
      eventTypes: [
        { value: "Wedding", label: "Boda" },
        { value: "Corporate Event", label: "Evento Corporativo" },
        { value: "Private Party", label: "Fiesta Privada" },
        { value: "Birthday", label: "Cumpleaños" },
        { value: "Holiday Party", label: "Fiesta de Temporada" },
        { value: "Cocktail Experience", label: "Experiencia de Cócteles" },
        { value: "Other", label: "Otro" },
      ],
      s1: { title: "¿Cuándo es tu evento?", hint: "Una fecha estimada está perfectamente bien.", dateLabel: "SELECCIONA LA FECHA", timeLabel: "HORA APROXIMADA DE INICIO", optional: "(OPCIONAL)", flexible: "Mi fecha es flexible" },
      s2: { title: "¿Dónde se llevará a cabo?", hint: "Lugar, vecindario o ciudad: atendemos NYC, Long Island y ubicaciones selectas del área Tri-State.", locationPh: "ej. Azotea en Tribeca, Manhattan", zipPh: "Ciudad / código postal" },
      s3: { title: "Número estimado de invitados" },
      guestCounts: [
        { value: "Under 30", label: "Menos de 30" },
        { value: "30–50", label: "30–50" },
        { value: "51–75", label: "51–75" },
        { value: "76–100", label: "76–100" },
        { value: "101–150", label: "101–150" },
        { value: "150+", label: "150+" },
      ],
      s4: { title: "¿Qué servicio estás considerando?", hint: "Elige lo más cercano; lo afinaremos juntos." },
      services: [
        { value: "Bartender Service", label: "Bartender Service", price: "DESDE $450" },
        { value: "Essential Bar Service", label: "Essential Bar Service", price: "DESDE $795" },
        { value: "Hudson & Stir Experience", label: "Hudson & Stir Experience", price: "DESDE $1,295" },
        { value: "Wedding / Large Event", label: "Boda / Evento Grande", price: "COTIZACIÓN PERSONALIZADA" },
        { value: "Not Sure Yet", label: "Aún no lo sé", price: "TE ORIENTAMOS" },
      ],
      s5: { title: "¿Cuánto tiempo nos necesitas?", hint: "Horas de servicio activo de bar; el montaje y el desmontaje ocurren fuera de este horario." },
      durations: [
        { value: "Up to 4 hours", label: "Hasta 4 horas" },
        { value: "5 hours", label: "5 horas" },
        { value: "6 hours", label: "6 horas" },
        { value: "7+ hours / Custom", label: "7+ horas / Personalizado" },
      ],
      s6: {
        title: "Algunos detalles del lugar.",
        hint: "Esto nos ayuda a planificar el montaje adecuado.",
        barLabel: "¿HAY UNA BARRA O MESA EN EL LUGAR?",
        barOptions: [
          { value: "Yes", label: "Sí" },
          { value: "No", label: "No" },
          { value: "Not sure", label: "No estoy seguro" },
        ],
        settingLabel: "¿INTERIOR O EXTERIOR?",
        settingOptions: [
          { value: "Indoor", label: "Interior" },
          { value: "Outdoor", label: "Exterior" },
          { value: "Both", label: "Ambos" },
          { value: "Not sure", label: "No estoy seguro" },
        ],
      },
      s7: { title: "¿Te interesa algún servicio adicional?", hint: "Opcional: selecciona lo que te interese. Los precios se confirman durante la planificación.", includedNote: "BARRA PORTÁTIL — INCLUIDA CON TU SERVICIO" },
      enhancements: [
        { value: "Portable Bar", label: "BARRA PORTÁTIL" },
        { value: "Additional Signature Cocktail", label: "SIGNATURE COCKTAIL ADICIONAL" },
        { value: "Zero-Proof Cocktail", label: "CÓCTEL SIN ALCOHOL" },
        { value: "Espresso Martini Service", label: "SERVICIO DE ESPRESSO MARTINI" },
        { value: "Champagne Toast", label: "BRINDIS CON CHAMPÁN" },
        { value: "Real Glassware", label: "CRISTALERÍA REAL" },
        { value: "Not Sure Yet", label: "AÚN NO LO SÉ" },
        { value: "None", label: "SIN ADICIONALES" },
      ],
      s8: { title: "Cuéntanos sobre tu evento.", hint: "La atmósfera que imaginas, estilos de bebidas o cócteles preferidos, los momentos importantes: todo ayuda.", optional: "OPCIONAL", placeholder: "Estamos planeando…" },
      s9: {
        title: "Tu información de contacto",
        firstPh: "Nombre",
        lastPh: "Apellido",
        emailPh: "Correo electrónico",
        phonePh: "Teléfono (opcional)",
        hint: "Por favor agrega tu nombre y un correo válido para poder contactarte.",
      },
      s10: {
        title: "Revisa tu solicitud",
        hint: "Una última mirada antes de empezar a planificar.",
        edit: "EDITAR",
        rows: { event: "EVENTO", date: "FECHA", location: "UBICACIÓN", guests: "INVITADOS", service: "SERVICIO", duration: "DURACIÓN", venue: "LUGAR", extras: "ADICIONALES", name: "NOMBRE", email: "CORREO", phone: "TELÉFONO" },
        flexibleSuffix: "(flexible)",
        barPrefix: "Barra/mesa:",
        note: "Hudson & Stir Mobile Bar es un servicio de bar dry-hire: el cliente proporciona las bebidas alcohólicas. Enviar una solicitud no reserva tu fecha; un acuerdo de servicio firmado y el anticipo confirman tu fecha.",
        privacyPre: "Al enviar esta solicitud aceptas nuestro",
        privacyLink: "Aviso de Privacidad.",
      },
      buttons: { back: "ATRÁS", continue: "CONTINUAR", submit: "ENVIAR SOLICITUD", sending: "ENVIANDO…" },
      sidebar: {
        quote: "Toda gran velada comienza con una conversación.",
        body: "Comparte algunos detalles de tu evento y te responderemos con una propuesta a tu medida. Sin cuentas, sin pagos: solo el inicio de algo memorable.",
      },
      confirm: {
        titlePre: "MUCHAS ",
        titleEm: "GRACIAS",
        line1: "¡Gracias por contactar a Hudson & Stir!",
        line2: "Recibimos los detalles de tu evento y nos pondremos en contacto contigo para conversar sobre tu celebración.",
        ref: "REFERENCIA",
        home: "VOLVER AL INICIO",
        gallery: "VER GALERÍA",
      },
      error: "Algo salió mal al enviar tu solicitud. Por favor inténtalo de nuevo.",
      venueJoin: " · ",
    },
    gate: {
      brand: "HUDSON & STIR",
      line1: "Choose your preferred language",
      line2: "Elige tu idioma de preferencia",
      en: "English",
      es: "Español",
      label: "Language selection",
    },
    privacy: {
      titlePre: "Aviso de ",
      titleEm: "Privacidad",
      intro: "Cómo Hudson & Stir Mobile Bar maneja la información que compartes con nosotros.",
      updated: "ÚLTIMA ACTUALIZACIÓN: AGOSTO 2026",
      sections: [
        { h: "QUÉ RECOPILAMOS", body: "Cuando envías una solicitud a través de nuestro formulario de cotización, recopilamos los datos que proporcionas: tu nombre, correo electrónico, teléfono (opcional), tu idioma preferido y los detalles de tu evento: tipo de evento, fecha y horario, ubicación, número de invitados, servicios y adicionales seleccionados, y cualquier nota que decidas compartir." },
        { h: "CÓMO LA USAMOS", body: "Usamos esta información únicamente para responder a tu solicitud, preparar una propuesta a tu medida, planificar y realizar tu evento, y gestionar nuestra relación de servicio contigo. No te añadimos a listas de marketing, y este sitio no utiliza rastreadores de publicidad." },
        { h: "QUIÉN LA MANEJA", body: "Un número reducido de proveedores de servicios procesa tu información en nuestro nombre: Resend entrega nuestros correos de notificación y confirmación, y nuestro proveedor de alojamiento web almacena las solicitudes en nuestra base de datos. También utilizamos analíticas de PostHog para entender cómo se usa el sitio (páginas visitadas e interacciones como clics en botones), lo que puede instalar una cookie o identificador similar en tu navegador." },
        { h: "CUÁNTO TIEMPO LA CONSERVAMOS", body: "Conservamos la información de las solicitudes durante el tiempo necesario para responder a tu petición, planificar y realizar tu evento, y cumplir con nuestras necesidades habituales de registro." },
        { h: "TUS OPCIONES", body: "Puedes pedirnos acceder, corregir o eliminar la información que has compartido con nosotros en cualquier momento escribiéndonos por correo. Responderemos a las solicitudes razonables con prontitud." },
        { h: "CONTACTO", body: "Hudson & Stir Mobile Bar · hello@hudsonandstir.com · Ciudad de Nueva York, Long Island y ubicaciones selectas del área Tri-State." },
      ],
    },
    lightbox: { close: "Cerrar galería", prev: "Imagen anterior", next: "Imagen siguiente", view: "Ver imagen ampliada:" },
    aria: { openMenu: "Abrir menú", closeMenu: "Cerrar menú", homeLogo: "Inicio de Hudson & Stir Mobile Bar" },
  },
};

